import { t } from "@lingui/macro";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, FileText, ChatTeardropText, Sparkle, X } from "@phosphor-icons/react";
import React from "react";

type NextStepsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onStartInterview?: () => void;
  onSkipToBuilder?: () => void;
  onContinueInterview?: () => void;
  onSendToBuilder?: () => void;
  jobRole: string;
  variant?: "post-extraction" | "post-interview";
};

export const NextStepsModal: React.FC<NextStepsModalProps> = ({
  isOpen,
  onClose,
  onStartInterview,
  onSkipToBuilder,
  onContinueInterview,
  onSendToBuilder,
  jobRole,
  variant = "post-extraction",
}) => {
  if (!isOpen) return null;

  const isPostInterview = variant === "post-interview";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-full overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-w-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 rounded-full p-2 text-white/90 transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label={t`Close`}
          >
            <X size={20} />
          </button>
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 p-4 text-center sm:p-8">
            <div className="absolute left-0 top-0 size-full overflow-hidden opacity-20">
              <div className="absolute left-0 top-0 size-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white blur-3xl"></div>
              <div className="absolute bottom-0 right-0 size-32 translate-x-1/2 translate-y-1/2 rounded-full bg-purple-400 blur-3xl"></div>
            </div>

            <div className="relative z-10">
              <div className="mb-4 inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 p-3 backdrop-blur-md">
                <Sparkle className="size-6 text-yellow-300" />
              </div>
              <h2 className="mb-2 text-3xl font-bold text-white">
                {isPostInterview ? t`Interview Complete!` : t`Market Analysis Complete!`}
              </h2>
              <p className="text-lg text-blue-100">
                {isPostInterview
                  ? t`Your ${jobRole} resume has been updated with interview insights.`
                  : t`We've gathered insights for your ${jobRole} resume.`}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-8">
            <p className="mx-auto mb-8 max-w-lg text-center text-gray-600">
              {isPostInterview
                ? t`Continue the interview to add more details, or send your resume to the builder to finalize.`
                : t`How would you like to proceed? You can simulate a real interview to tailor your resume perfectly, or jump straight into the builder.`}
            </p>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Option 1: AI Interview - COMING SOON (disabled) */}
              <div
                className="group relative flex cursor-not-allowed flex-col items-center rounded-xl border-2 border-gray-200 bg-gray-50 p-6 text-center opacity-60"
              >
                <div className="absolute right-3 top-3">
                  <span className="rounded-full bg-gray-400 px-2 py-1 text-xs font-bold text-white">
                    {t`Coming Soon`}
                  </span>
                </div>
                <div className="mb-4 rounded-full bg-gray-200 p-4">
                  <ChatTeardropText className="size-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-400">
                  {isPostInterview ? t`Continue Interview` : t`Start AI Interview`}
                </h3>
                <p className="mb-4 text-sm text-gray-400">
                  {t`This feature is under development. Stay tuned!`}
                </p>
                <div className="mt-auto flex items-center text-sm font-semibold text-gray-400">
                  {t`Coming Soon`}
                </div>
              </div>

              {/* Option 2: Builder */}
              <button
                className="group flex flex-col items-center rounded-xl border-2 border-gray-100 p-6 text-center transition-all hover:border-gray-400 hover:bg-gray-50"
                onClick={isPostInterview ? onSendToBuilder : onSkipToBuilder}
              >
                <div className="mb-4 rounded-full bg-gray-100 p-4 transition-colors group-hover:bg-gray-200">
                  <FileText className="size-8 text-gray-600" />
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-900">
                  {isPostInterview ? t`Send to Builder` : t`Go to Builder`}
                </h3>
                <p className="mb-4 text-sm text-gray-500">
                  {isPostInterview
                    ? t`Save your interview results and edit in the builder.`
                    : t`Skip the interview and edit your resume manually in the builder.`}
                </p>
                <div className="mt-auto flex items-center text-sm font-semibold text-gray-600 transition-transform group-hover:translate-x-1">
                  {t`Open Builder`} <ArrowRight className="ml-1 size-4" />
                </div>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
